// ── Trello card picker overlay (C1448) ──
//
// Single-card picker used by the New Task modal's Import ▾ ▸ From Trello row to prefill
// Title/Description in place. Deliberately NOT built on ui.js's openModal()/closeModal() —
// see lib/import-picker-shell.js's header comment for the full reasoning (single-modal
// system, reserved-key release). The shared overlay/backdrop/header/Escape/mousedown-guard
// chrome lives there (extracted C1449, alongside the Gmail thread picker); this file only
// implements the Trello-specific steps.
//
// Also deliberately NOT reusing openTrelloImportModal() (trello-import-modal.js): that
// flow is multi-select and ends in POST /projects/:id/trello/import, which creates
// TR-<shortLink> tasks. This picker never creates a task — it only reads one card and
// hands its content back to the caller.
//
// (C1552) The workspace/board chained dropdowns and the "current board + Change" bar are
// shared with trello-import-modal.js via lib/trello-scope-picker.js — see that file's
// header for why they live there and not in lib/import-picker-shell.js. The board is no
// longer a one-time, permanent choice: renderBoardStep() is re-enterable from the card list
// at any time via the scope bar's Change button, and getting there always lands back on
// the card list — a viewer whose association POST 403s gets a session-only override
// instead of a dead end.

import { api } from '../api.js';
import { escapeHtml } from '../utils.js';
import { createImportPicker, notConfiguredFrom, setupNoticeHtml } from '../lib/import-picker-shell.js';
import { renderTrelloScopePickers, renderTrelloScopeBar, resolveEffectiveScope, matchesCardQuery } from '../lib/trello-scope-picker.js';

const esc = escapeHtml;

// Trello attachment `url`s returned by GET /trello/cards/:cardId are Trello download URLs
// requiring OAuth 1.0a signing — rewrite to the same-origin proxy so an <img> can render
// them without hand-signing. Ported from ai/todo/server/src/client/task-board.js:1528-1536
// (the Task App's Objective-chat wizard); nothing web-side had this before (C1448).
const TRELLO_INLINE_IMAGE_RE = /https:\/\/(?:api\.)?trello\.com\/1\/cards\/[^\s)<>"']*?\/download\/[^\s)<>"']+/g;

function trelloImageProxyUrl(url) {
  return `/api/trello/image-proxy?url=${encodeURIComponent(url)}`;
}

function rewriteTrelloInlineImages(text) {
  return String(text || '').replace(TRELLO_INLINE_IMAGE_RE, (url) => trelloImageProxyUrl(url));
}

// Ported from task-board.js:1701-1709.
function buildChecklistMd(checklists) {
  if (!checklists || !checklists.length) return '';
  return checklists.map((cl) => {
    const items = (cl.checkItems || []).map((ci) =>
      `- [${ci.state === 'complete' ? 'x' : ' '}] ${ci.name}`
    ).join('\n');
    return `**${cl.name}**\n${items}`;
  }).join('\n\n');
}

function labelChip(l) {
  const bg = l.color ? `var(--trello-${esc(l.color)}, var(--c-overlay1))` : 'var(--c-overlay1)';
  return `<span style="background:${bg};color:#fff;font-size:.7rem;padding:1px 7px;border-radius:9px;white-space:nowrap">${esc(l.name || l.color || '')}</span>`;
}

function cardListHTML(list) {
  if (!list.length) return '<p class="import-picker-status">No cards found</p>';
  return list.map((c) => {
    const chips = (c.labels || []).filter((l) => l.name || l.color).map(labelChip).join('');
    return `<div class="trello-card-row" data-card-id="${esc(c.id)}" tabindex="0" role="button">
      <span style="flex:1;font-size:.9rem">${esc(c.name)}</span>
      ${c.listName ? `<span class="trello-card-list-badge">${esc(c.listName)}</span>` : ''}
      <span class="trello-card-status-badge ${c.dueComplete ? 'done' : 'open'}">${c.dueComplete ? 'Done' : 'Open'}</span>
      ${chips ? `<span style="display:flex;gap:4px;flex-wrap:wrap">${chips}</span>` : ''}
    </div>`;
  }).join('');
}

// openTrelloCardPicker(projectId, { onPick }) -> void
//   onPick({ title, description }) — called once, right before the overlay closes itself.
export function openTrelloCardPicker(projectId, { onPick } = {}) {
  const ui = createImportPicker({ title: 'Import from Trello' });
  const { body, setBody, statusHtml, errorHtml, showInlineError, onWindowFocusOnce, close } = ui;

  // association — the project's saved default (project_trello_boards). override — this
  // picker session's own choice, if any (always wins; see resolveEffectiveScope). notice —
  // a one-shot message to surface the next time the card list renders (e.g. the 403
  // session-only fallback), drained by renderCardsStep().
  const state = { projectId, onPick, association: null, override: null, notice: null, cards: [] };

  function currentScope() {
    return resolveEffectiveScope(state.association, state.override);
  }

  // ── Step 0: resolve connection + saved board association ──
  async function renderResolveStep() {
    setBody(statusHtml('Checking connection…'));
    let statusRes, assocRes;
    try {
      [statusRes, assocRes] = await Promise.all([
        api.get('/trello/status'),
        api.get(`/projects/${state.projectId}/trello/board-association`).catch(() => ({ association: null })),
      ]);
    } catch (err) {
      if (ui.destroyed) return;
      // C1551 — err.message here would be the raw token (trello_not_configured) on an
      // unconfigured server: this picker never had an error-code map, unlike Gmail/Jira's.
      const notConfigured = notConfiguredFrom(err);
      setBody(notConfigured ? setupNoticeHtml(notConfigured) : errorHtml(err.message || 'Failed to check Trello connection.'));
      return;
    }
    if (ui.destroyed) return;

    if (!statusRes.connected) { renderConnectStep(); return; }

    state.association = assocRes.association || null;
    if (currentScope()) renderCardsStep();
    else renderBoardStep({ canGoBack: false });
  }

  // ── Step 1: connect ──
  function renderConnectStep() {
    setBody(`
      <p style="margin:0 0 12px">Connect your Trello account to import a card.</p>
      <p style="margin:0 0 16px;font-size:.85rem;color:var(--c-overlay1)">Opens Trello in a new tab — this window stays open.</p>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="trello-picker-cancel">Cancel</button>
        <button class="btn btn-primary" id="trello-picker-connect">Connect Trello</button>
      </div>`);

    body.querySelector('#trello-picker-cancel').addEventListener('click', close);
    body.querySelector('#trello-picker-connect').addEventListener('click', async () => {
      const btn = body.querySelector('#trello-picker-connect');
      btn.disabled = true; btn.textContent = 'Opening…';
      // Open the tab synchronously, inside the click gesture — Safari/Firefox block
      // window.open() called after an await (the user-gesture chain is already broken by
      // then), so the real navigation target is assigned once the URL comes back.
      const w = window.open('', '_blank');
      try {
        const { url } = await api.get('/trello/auth/initiate');
        if (ui.destroyed) { w?.close(); return; }
        if (w) { w.opener = null; w.location = url; }
        renderWaitingStep(url);
      } catch (err) {
        w?.close();
        if (ui.destroyed) return;
        const notConfigured = notConfiguredFrom(err);
        if (notConfigured) { setBody(setupNoticeHtml(notConfigured)); return; }
        btn.disabled = false; btn.textContent = 'Connect Trello';
        showInlineError(err.message || 'Failed to start Trello authorization.');
      }
    });
  }

  function renderWaitingStep(url) {
    setBody(`
      <p style="margin:0 0 12px">Finish authorizing in the other tab, then come back here.</p>
      <p style="margin:0 0 16px;font-size:.85rem"><a href="${esc(url)}" target="_blank" rel="noopener">Reopen the Trello authorization page</a></p>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="trello-picker-cancel">Cancel</button>
        <button class="btn btn-primary" id="trello-picker-retry">I've connected — Continue</button>
      </div>`);
    body.querySelector('#trello-picker-cancel').addEventListener('click', close);
    body.querySelector('#trello-picker-retry').addEventListener('click', renderResolveStep);

    // Auto-retry once the user tabs back to this window.
    onWindowFocusOnce(renderResolveStep);
  }

  // ── Step 2: board select — re-enterable via the scope bar's Change button ──
  function renderBoardStep({ canGoBack = false } = {}) {
    setBody('<div id="trello-picker-scope-root"></div>');
    const root = body.querySelector('#trello-picker-scope-root');
    const scope = currentScope();

    renderTrelloScopePickers(root, {
      idPrefix: 'trello-picker',
      initial: scope ? { workspaceId: scope.workspaceId, boardId: scope.boardId } : {},
      confirmLabel: 'Continue',
      cancelLabel: canGoBack ? 'Back' : 'Cancel',
      onConfirm: (selection) => commitScope(selection),
      onCancel: () => { if (canGoBack) renderCardsStep(); else close(); },
      onError: (msg) => showInlineError(msg),
    });
  }

  // Always attempts to persist the project default; a viewer's 403 (or any other save
  // failure) degrades to a session-only override rather than blocking the import — see
  // ai/architecture/tt-trello-integration.md's C1552 section for the reasoning. Never
  // throws: renderTrelloScopePickers only needs to handle a throw here for a genuinely
  // unexpected failure, which this function doesn't produce.
  async function commitScope({ workspaceId, boardId, boardName }) {
    try {
      await api.post(`/projects/${state.projectId}/trello/board-association`, {
        trello_workspace_id: workspaceId,
        trello_board_id: boardId,
        trello_board_name: boardName,
      });
      if (ui.destroyed) return;
      state.association = { trello_workspace_id: workspaceId, trello_board_id: boardId, trello_board_name: boardName };
      state.override = { workspaceId, boardId, boardName, persisted: true };
    } catch (err) {
      if (ui.destroyed) return;
      state.override = { workspaceId, boardId, boardName, persisted: false };
      state.notice = err.status === 403
        ? 'Only project editors can set the project default board — using this board for this session only.'
        : (err.message ? `Could not save this board as the project default (${err.message}) — using it for this session only.` : 'Could not save this board as the project default — using it for this session only.');
    }
    if (ui.destroyed) return;
    renderCardsStep();
  }

  // ── Step 3: single-select card list + search, scoped to the current board ──
  async function renderCardsStep() {
    setBody(statusHtml('Loading cards…'));
    const scope = currentScope();
    let cards;
    try {
      cards = await api.get('/trello/cards?boardId=' + encodeURIComponent(scope.boardId));
    } catch (err) {
      if (ui.destroyed) return;
      const notConfigured = notConfiguredFrom(err);
      if (notConfigured) { setBody(setupNoticeHtml(notConfigured)); return; }
      setBody(errorHtml(err.message || 'Failed to load cards.') +
        `<div class="modal-footer"><button class="btn btn-ghost" id="trello-picker-change-board">Change board</button></div>`);
      body.querySelector('#trello-picker-change-board').addEventListener('click', () => renderBoardStep({ canGoBack: true }));
      return;
    }
    if (ui.destroyed) return;

    state.cards = cards;
    setBody(`
      ${renderTrelloScopeBar({ boardName: scope.boardName, boardId: scope.boardId, persisted: scope.persisted, changeId: 'trello-picker-change-board' })}
      <div class="form-group">
        <input class="form-input" id="trello-picker-search" type="search" placeholder="Search cards by name or description…">
      </div>
      <div class="trello-card-list" id="trello-picker-list">${cardListHTML(cards)}</div>`);

    body.querySelector('#trello-picker-change-board').addEventListener('click', () => renderBoardStep({ canGoBack: true }));

    const list = body.querySelector('#trello-picker-list');
    const searchInput = body.querySelector('#trello-picker-search');

    searchInput.addEventListener('input', () => {
      const q = searchInput.value.trim();
      const filtered = q ? cards.filter((c) => matchesCardQuery(c, q)) : cards;
      list.innerHTML = cardListHTML(filtered);
    });

    // Delegated click — one listener covers every row across search re-renders.
    list.addEventListener('click', (e) => {
      const row = e.target.closest('.trello-card-row');
      if (!row || list.classList.contains('is-busy')) return;
      pickCard(row.dataset.cardId, row);
    });

    // One-shot notice from a just-completed board change (e.g. the 403 session-only
    // fallback) — must run after setBody() above, not before, or it would be wiped.
    if (state.notice) {
      showInlineError(state.notice);
      state.notice = null;
    }
  }

  async function pickCard(cardId, row) {
    const list = body.querySelector('#trello-picker-list');
    list.classList.add('is-busy');
    row.classList.add('is-picking');

    let detail;
    try {
      detail = await api.get('/trello/cards/' + encodeURIComponent(cardId));
    } catch (err) {
      if (ui.destroyed) return;
      list.classList.remove('is-busy');
      row.classList.remove('is-picking');
      const notConfigured = notConfiguredFrom(err);
      showInlineError(notConfigured ? `Trello import isn't configured on this server (missing ${notConfigured.missing.join(', ')}).` : (err.message || 'Failed to load card.'));
      return;
    }
    if (ui.destroyed) return;

    // Project-scoped endpoint first: it materializes real project images
    // (/api/projects/:pid/images/:id), the only URL shape attachment-preview.js's regex
    // matches — those show up as thumbnail chips in the New Task modal. requireWriteAccess
    // means a viewer gets 403 here; that's an expected fallback, not an error, so it's
    // swallowed silently and the proxy URLs below cover it.
    let uploaded = [];
    try {
      const imgRes = await api.get(`/projects/${state.projectId}/trello/cards/${encodeURIComponent(cardId)}/images`);
      uploaded = Array.isArray(imgRes.images) ? imgRes.images : [];
    } catch (_) { /* not_connected / 403 viewer / upstream failure — fall back below */ }
    if (ui.destroyed) return;

    const desc = rewriteTrelloInlineImages(detail.desc || '');
    const checklistMd = buildChecklistMd(detail.checklists);
    const attMd = uploaded.length
      ? uploaded.map((img) => `![${img.name || 'attachment'}](${img.url})`).join('\n')
      : (detail.attachments || []).filter((a) => a?.url)
          .map((a) => `![${a.name || 'attachment'}](${trelloImageProxyUrl(a.url)})`).join('\n');

    const parts = [];
    if (desc) parts.push(desc);
    if (checklistMd) parts.push(checklistMd);
    if (attMd) parts.push(attMd);
    // Provenance line last (C1553): leading with it made a card with an empty desc look like
    // the import had produced nothing but a URL. Still deliberately NOT the <!-- trello:start
    // --> markers — those are owned by the server's applyTrelloCardToTask re-import path and
    // would read as server-managed on a hand-created task.
    parts.push(`**Trello card:** ${detail.url}`);

    const payload = { title: detail.name || '', description: parts.join('\n\n') };
    close();
    if (state.onPick) state.onPick(payload);
  }

  renderResolveStep();
}
